import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { AnatomicalSideEnum, BoneTypeEnum, DeviceEnum, PlanTypeEnum, ReferenceTypeEnum, setRequired } from '../core';
import { Attachment } from './attachment';


const SPECIAL_CHARS_PATTERN: RegExp = /^[^<>%#&?,]+$/;
const SPECIAL_CHARS_TEXT = '<>%#&?,';
const SPECIAL_CHARS_LITE_PATTERN: RegExp = /^[^<>%#?]+$/;
const SPECIAL_CHARS_LITE_TEXT = '<>%#?';
const planId_maxLength: number = 50;
const notes_maxLength: number = 1000;
const anatomicalSite_invalid = /^0*?[1-9]\d*$/; // fake pattern regex
const image_invalid = /^0*?[1-9]\d*$/; // fake pattern regex

export const planIdNotUnique = 'planIdNotUnique';


export class PlanControl implements IPlanControl {
	patientGuid = new FormControl<string | null>(null, [Validators.required]);
	planId = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(planId_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	notes = new FormControl<string | null>(null, [Validators.maxLength(notes_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]);
	device = new FormControl<DeviceEnum | null>(null, [Validators.required]);
	postOperative = new FormControl<boolean | null>(null); // conditional required
	planType = new FormControl<PlanTypeEnum | null>(null); // conditional required
	referenceType = new FormControl<ReferenceTypeEnum | null>(null); // conditional required
	withImages = new FormControl<boolean | null>(null, [Validators.required]);
	anatomicalSite = new FormControl<AnatomicalSiteEnum | null>(null, [Validators.required]);
	side = new FormControl<AnatomicalSideEnum | null>(null, [Validators.required]);
	boneType = new FormControl<BoneTypeEnum | null>(null, [Validators.required]);
	apImage = new FormControl<Attachment | null>(null);
	ltImage = new FormControl<Attachment | null>(null);
}

export function setPlanValidators(form: FormGroup<IPlanControl>): void {
	form.controls.device.valueChanges.subscribe(device => {
		setRequired(form.controls.postOperative, device === DeviceEnum.Tlhex);
		setRequired(form.controls.planType, device === DeviceEnum.Tlhex);
		setRequired(form.controls.referenceType, device === DeviceEnum.Tlhex);
	});
	combineLatest([form.controls.device.valueChanges, form.controls.boneType.valueChanges]).subscribe(([device, bone]) => {
		const ctrl = form.controls.anatomicalSite;
		const validators = [Validators.required];
		isBoneInvalid(bone, device) && validators.push(Validators.pattern(anatomicalSite_invalid));
		ctrl.setValidators(validators);
		ctrl.updateValueAndValidity({ emitEvent: false });
	});
	combineLatest([
		form.controls.device.valueChanges,
		form.controls.withImages.valueChanges
	]).subscribe(([device, withImages]) => updateImageValidators(form, { device, withImages }));
	form.controls.boneType.valueChanges.subscribe(bone => updateImageValidators(form, { bone }));
	form.controls.side.valueChanges.subscribe(side => updateImageValidators(form, { side }));
	form.controls.apImage.valueChanges.subscribe(image => updateImageValidators(form, { AP: image }));
	form.controls.ltImage.valueChanges.subscribe(image => updateImageValidators(form, { LT: image }));
}

function updateImageValidators(
	form: FormGroup<IPlanControl>,
	newData: {
		device?: DeviceEnum | null;
		withImages?: boolean | null;
		bone?: BoneTypeEnum | null;
		side?: AnatomicalSideEnum | null;
		AP?: Attachment | null;
		LT?: Attachment | null;
	}
): void {
	const device = newData.device === undefined ? form.value.device : newData.device;
	const withImages = newData.withImages === undefined ? form.value.withImages : newData.withImages;
	const bone = newData.bone === undefined ? form.value.boneType : newData.bone;
	const side = newData.side === undefined ? form.value.side : newData.side;
	const AP = newData.AP === undefined ? form.value.apImage : newData.AP;
	const LT = newData.LT === undefined ? form.value.ltImage : newData.LT;
	// AP validation
	const apValidators = [];
	const isApRequired = !!withImages && (device !== DeviceEnum.Jps || !LT);
	const isApMismatch = !!withImages && !!AP && (AP.boneType !== bone || AP.boneSide !== side);
	isApRequired && apValidators.push(Validators.required);
	isApMismatch && apValidators.push(Validators.pattern(image_invalid));
	form.controls.apImage.setValidators(apValidators);
	form.controls.apImage.updateValueAndValidity({ emitEvent: false });
	// LT validation
	const ltValidators = [];
	const isLtRequired = !!withImages && (device == DeviceEnum.Tlhex || (device === DeviceEnum.Jps && !AP));
	const isLtMismatch = !!withImages && !!LT && (LT.boneType !== bone || LT.boneSide !== side);
	isLtRequired && ltValidators.push(Validators.required);
	isLtMismatch && ltValidators.push(Validators.pattern(image_invalid));
	form.controls.ltImage.setValidators(ltValidators);
	form.controls.ltImage.updateValueAndValidity({ emitEvent: false });
}

export function isBoneInvalid(bone: BoneTypeEnum | null, device: DeviceEnum | null): boolean {
	return !bone || (device === DeviceEnum.Fitbone && bone !== BoneTypeEnum.LongLeg)
		|| (device === DeviceEnum.Jps && [BoneTypeEnum.Ankle, BoneTypeEnum.Forefoot, BoneTypeEnum.Hindfoot].includes(bone));
}

export const planErrorMsgs: { [key in keyof PlanForm]: { [y: string]: string[] } | null } = {
	patientGuid: null,
	planId: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PLANID'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PLANID', planId_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PLANID', SPECIAL_CHARS_TEXT],
		[`${planIdNotUnique}`]: ['VALIDATION_MESSAGE_UNIQUE_PLANID']
	},
	notes: {
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PLAN_NOTES', notes_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PLAN_NOTES', SPECIAL_CHARS_LITE_TEXT]
	},
	device: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_DEVICE'],
	},
	postOperative: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PLANSCENARIO'],
	},
	planType: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PLANTYPE'],
	},
	referenceType: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_REFERENCESEGMENT'],
	},
	withImages: null,
	anatomicalSite: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PLAN_ANATOMICAL_SITE'],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_INVALID_BONETYPE']
	},
	side: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PLAN_ANATOMICAL_SITE']
	},
	boneType: null,
	apImage: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_IMAGE_AP'],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_INVALID_IMAGE_DATA']
	},
	ltImage: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_IMAGE_LAT'],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_INVALID_IMAGE_DATA']
	}
}

export interface IPlanControl {
	patientGuid: FormControl<string | null>;
	planId: FormControl<string | null>;
	notes: FormControl<string | null>;
	device: FormControl<DeviceEnum | null>;
	postOperative: FormControl<boolean | null>;
	planType: FormControl<PlanTypeEnum | null>;
	referenceType: FormControl<ReferenceTypeEnum | null>;
	withImages: FormControl<boolean | null>;
	anatomicalSite: FormControl<AnatomicalSiteEnum | null>;
	side: FormControl<AnatomicalSideEnum | null>;
	boneType: FormControl<BoneTypeEnum | null>;
	apImage: FormControl<Attachment | null>;
	ltImage: FormControl<Attachment | null>;
}

export interface PlanForm {
	patientGuid: string;
	planId: string;
	notes: string | null;
	device: DeviceEnum;
	postOperative: boolean;
	planType: PlanTypeEnum;
	referenceType: ReferenceTypeEnum;
	withImages: boolean | null;
	anatomicalSite: AnatomicalSiteEnum | null;
	side: AnatomicalSideEnum;
	boneType: BoneTypeEnum;
	apImage?: Attachment | null;
	ltImage?: Attachment | null;
}

export enum AnatomicalSiteEnum {
	RightLongLeg = 'RightLongLeg',
	RightFemur = 'RightFemur',
	RightTibia = 'RightTibia',
	RightAnkle = 'RightAnkle',
	RightForefoot = 'RightForefoot',
	RightHindfoot = 'RightHindfoot',
	LeftLongLeg = 'LeftLongLeg',
	LeftFemur = 'LeftFemur',
	LeftTibia = 'LeftTibia',
	LeftAnkle = 'LeftAnkle',
	LeftForefoot = 'LeftForefoot',
	LeftHindfoot = 'LeftHindfoot'
}
